import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { MomentumRingAvatar } from "@/components/rank/momentum-ring-avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { creatorFeedCheerPath } from "@/routes"
import {
  CHEER_MESSAGE_MAX,
  CHEER_REACTIONS,
  CHEER_REACTION_META,
  type CheerReaction,
} from "@/types/cheers"

/**
 * OKS-555 — §17 CheerComposeSheet (`ui/drawer` bottom sheet).
 *
 * Replaces the OKS-509 instant one-tap 🔥: tapping the feed cheer button opens
 * this sheet to pick a reaction (🔥/👏/🙌/💯) + add an optional ≤140 message,
 * then Send. Recipient identity (real photo + momentum ring, OKS-512) + a post
 * thumbnail/caption give the cheer context.
 *
 * ## Crew form rule
 * Inertia `<Form>` from `@inertiajs/react` (NOT shadcn Form / RHF / zod) POSTing
 * `creatorFeedCheerPath(submissionId)` with plain `name`d fields: a hidden
 * `reaction` (driven by the radiogroup state) + the `message` Textarea. js-routes
 * for the URL.
 *
 * ## Optimistic send (UX Spec §3)
 * On submit START the feed reaction fills OPTIMISTICALLY (`onOptimisticSend`) —
 * we don't wait on the server. On SUCCESS the sheet shows a brief "Sent"
 * confirmation then auto-dismisses. On ERROR the parent rolls the feed reaction
 * back (`onSendError`) and the sheet stays open to retry.
 *
 * Locked testids: `cheer-compose-sheet`, `cheer-reaction-{fire|clap|raise|hundred}`,
 * `cheer-message`, `cheer-send`, `cheer-sent`.
 */
export interface CheerComposeSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  submissionId: number
  /** Recipient (post author) identity for the mini-card. */
  recipient: {
    name: string
    handle: string | null
    avatarUrl: string | null
    rankName: string | null
  }
  postThumbnailUrl: string | null
  postCaption: string
  /** Preselected reaction (the viewer's current one, or "fire" default). */
  initialReaction: CheerReaction
  /** Fill the feed reaction optimistically at submit start. */
  onOptimisticSend: (reaction: CheerReaction) => void
  /** Roll the optimistic feed fill back on a failed send. */
  onSendError: () => void
}

export function CheerComposeSheet({
  open,
  onOpenChange,
  submissionId,
  recipient,
  postThumbnailUrl,
  postCaption,
  initialReaction,
  onOptimisticSend,
  onSendError,
}: CheerComposeSheetProps) {
  const { t } = useTranslation()
  const [reaction, setReaction] = useState<CheerReaction>(initialReaction)
  const [message, setMessage] = useState("")
  const [sent, setSent] = useState(false)

  const handle = recipient.handle ? `@${recipient.handle}` : recipient.name
  const remaining = CHEER_MESSAGE_MAX - message.length

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="cheer-compose-sheet">
        <DrawerHeader>
          <DrawerTitle>{t("cheers.compose.title")}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {t("cheers.compose.description", {
              name: recipient.name || handle,
            })}
          </DrawerDescription>
        </DrawerHeader>

        {/* Mini recipient card + post context. */}
        <div className="px-4">
          <div className="bg-muted flex items-center gap-3 rounded-md p-3">
            <MomentumRingAvatar
              src={recipient.avatarUrl}
              name={recipient.name || handle}
              ring="full"
              size="sm"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold">{handle}</span>
              {recipient.rankName && (
                <Badge
                  variant="secondary"
                  className="w-fit px-1.5 py-0 text-[10px] font-medium"
                >
                  {recipient.rankName}
                </Badge>
              )}
            </div>
            {postThumbnailUrl && (
              <div className="bg-muted-foreground/10 aspect-[72/100] w-[54px] shrink-0 overflow-hidden rounded-md">
                <img
                  src={postThumbnailUrl}
                  alt={postCaption || t("cheers.compose.thumbnail_alt")}
                  className="size-full object-cover"
                />
              </div>
            )}
          </div>
        </div>

        {/* Quick-reacts radiogroup + message field. */}
        <div className="flex flex-col gap-4 px-4 pt-4">
          <div
            role="radiogroup"
            aria-label={t("cheers.compose.reaction_group")}
            className="flex justify-between gap-2"
          >
            {CHEER_REACTIONS.map((key) => {
              const meta = CHEER_REACTION_META[key]
              const selected = reaction === key
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`cheer-reaction-${key}`}
                  onClick={() => setReaction(key)}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-1 rounded-lg border p-2 transition-transform",
                    "min-h-[64px] justify-center focus-visible:ring-2 focus-visible:outline-none",
                    selected
                      ? "scale-105 border-orange-300 bg-orange-50 dark:bg-orange-950/40"
                      : "border-transparent",
                  )}
                >
                  <span className="text-2xl" aria-hidden="true">
                    {meta.emoji}
                  </span>
                  <span className="text-muted-foreground text-[11px] font-medium">
                    {t(meta.labelKey)}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cheer-message-input" className="sr-only">
              {t("cheers.compose.message_label")}
            </Label>
            <Textarea
              id="cheer-message-input"
              data-testid="cheer-message"
              value={message}
              onChange={(e) =>
                setMessage(e.target.value.slice(0, CHEER_MESSAGE_MAX))
              }
              maxLength={CHEER_MESSAGE_MAX}
              placeholder={t("cheers.compose.message_placeholder")}
              rows={3}
            />
            <span
              aria-live="polite"
              className={cn(
                "self-end text-[11px] tabular-nums",
                remaining <= 20
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-muted-foreground",
              )}
            >
              {t("cheers.compose.counter", {
                count: message.length,
                max: CHEER_MESSAGE_MAX,
              })}
            </span>
          </div>
        </div>

        <DrawerFooter>
          <Form
            action={creatorFeedCheerPath(submissionId)}
            method="post"
            options={{ preserveScroll: true, preserveState: true }}
            onStart={() => onOptimisticSend(reaction)}
            onSuccess={() => {
              setSent(true)
              window.setTimeout(() => {
                setSent(false)
                onOpenChange(false)
              }, 900)
            }}
            onError={onSendError}
          >
            {({ processing }) => (
              <>
                <input
                  type="hidden"
                  name="reaction"
                  value={reaction}
                  readOnly
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={processing || sent}
                  data-testid="cheer-send"
                >
                  {sent ? (
                    <span data-testid="cheer-sent">
                      {t("cheers.compose.sent", {
                        emoji: CHEER_REACTION_META[reaction].emoji,
                      })}
                    </span>
                  ) : (
                    t("cheers.compose.send")
                  )}
                </Button>
              </>
            )}
          </Form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
